import React from 'react';

import { NavigationExternalItem } from '@app/router';
import { UserRoleEnum } from '@app/types';
import {
  CustomDropdownItem,
  CustomLinkItem,
  CustomSectionItem,
  SystemDropdownItem,
  SystemLinkItem,
  SystemSectionItem,
  UserSectionItem,
} from '@app/gen/side_menu';

import {
  SidebarLinkItem,
  SidebarCustomLinkItem,
  SidebarSectionItem,
  SidebarCustomSectionItem,
  SidebarSystemDropdownItem,
  SidebarCustomDropdownItem,
  SidebarUserSectionItem,
  SidebarWidgetItem,
} from '../SidebarItems';

export type MenuItem =
  | SystemLinkItem
  | SystemDropdownItem
  | SystemSectionItem
  | CustomSectionItem
  | CustomLinkItem
  | CustomDropdownItem
  | UserSectionItem;

interface SidebarItemMapperProps {
  item: MenuItem;
  sidebarOpened: boolean;
  isSkeletonLoading: boolean;
  isActive: (path: string, href: string) => boolean;
  pathname: string;
  additionalOptions?: Record<UserRoleEnum, NavigationExternalItem[]>;
  onMenuItemClick: (pageTitle: string) => void;
  setSubmenusVisibleItemsCount?: (count: number) => void;
}

export const isSystemLinkItem = (item: MenuItem): item is SystemLinkItem =>
  item.type === 'link_item';

const isCustomLinkItem = (item: MenuItem): item is CustomLinkItem =>
  item.type === 'custom_link_item';

export const isSystemSectionItem = (
  item: MenuItem,
): item is SystemSectionItem => item.type === 'section_item';

const isCustomSectionItem = (item: MenuItem): item is CustomSectionItem =>
  item.type === 'custom_section_item';

export const isSystemDropdownItem = (
  item: MenuItem,
): item is SystemDropdownItem => item.type === 'dropdown_item';

export const isCustomDropdownItem = (
  item: MenuItem,
): item is CustomDropdownItem => item.type === 'custom_dropdown_item';

const isUserSectionItem = (item: MenuItem): item is UserSectionItem =>
  item.type === 'user_section_item';

export const sidebarItemMapper = {
  link_item: ({
    item,
    pathname,
    sidebarOpened,
    isSkeletonLoading,
    onMenuItemClick,
    isActive,
  }: SidebarItemMapperProps) => {
    if (!isSystemLinkItem(item)) return null;

    return (
      <SidebarLinkItem
        key={item.nick}
        item={item}
        pathname={pathname}
        sidebarOpened={sidebarOpened}
        isSkeletonLoading={isSkeletonLoading}
        onMenuItemClick={onMenuItemClick}
        isActive={isActive}
      />
    );
  },

  custom_link_item: ({
    item,
    sidebarOpened,
    pathname,
    isSkeletonLoading,
    onMenuItemClick,
    isActive,
  }: SidebarItemMapperProps) => {
    if (!isCustomLinkItem(item)) return null;

    if (item.external_system) {
      return (
        <SidebarWidgetItem
          key={item.link}
          item={item}
          pathname={pathname}
          sidebarOpened={sidebarOpened}
          isSkeletonLoading={isSkeletonLoading}
          onMenuItemClick={onMenuItemClick}
          isActive={isActive}
        />
      );
    }

    return (
      <SidebarCustomLinkItem
        key={item.link}
        item={item}
        pathname={pathname}
        sidebarOpened={sidebarOpened}
        isSkeletonLoading={isSkeletonLoading}
        onMenuItemClick={onMenuItemClick}
        isActive={isActive}
      />
    );
  },

  section_item: ({
    item,
    sidebarOpened,
    isSkeletonLoading,
    onMenuItemClick,
    additionalOptions,
  }: SidebarItemMapperProps) => {
    if (!isSystemSectionItem(item)) return null;

    return (
      <SidebarSectionItem
        key={item.nick}
        item={item}
        sidebarOpened={sidebarOpened}
        isSkeletonLoading={isSkeletonLoading}
        onMenuItemClick={onMenuItemClick}
        additionalOptions={additionalOptions}
      />
    );
  },

  custom_section_item: ({
    item,
    sidebarOpened,
    isSkeletonLoading,
    onMenuItemClick,
  }: SidebarItemMapperProps) => {
    if (!isCustomSectionItem(item)) return null;

    return (
      <SidebarCustomSectionItem
        key={item.name}
        item={item}
        sidebarOpened={sidebarOpened}
        isSkeletonLoading={isSkeletonLoading}
        onMenuItemClick={onMenuItemClick}
      />
    );
  },

  dropdown_item: ({
    item,
    sidebarOpened,
    isActive,
    isSkeletonLoading,
    onMenuItemClick,
    pathname,
    setSubmenusVisibleItemsCount,
  }: SidebarItemMapperProps) => {
    if (!isSystemDropdownItem(item)) return null;

    return (
      <SidebarSystemDropdownItem
        key={item.nick}
        item={item}
        sidebarOpened={sidebarOpened}
        isActive={isActive}
        skeletonLoading={isSkeletonLoading}
        onMenuItemClick={onMenuItemClick}
        pathname={pathname}
        setSubmenusVisibleItemsCount={setSubmenusVisibleItemsCount}
      />
    );
  },

  custom_dropdown_item: ({
    item,
    sidebarOpened,
    isActive,
    isSkeletonLoading,
    onMenuItemClick,
    pathname,
    setSubmenusVisibleItemsCount,
  }: SidebarItemMapperProps) => {
    if (!isCustomDropdownItem(item)) return null;

    return (
      <SidebarCustomDropdownItem
        key={item.name}
        item={item}
        sidebarOpened={sidebarOpened}
        isActive={isActive}
        skeletonLoading={isSkeletonLoading}
        onMenuItemClick={onMenuItemClick}
        pathname={pathname}
        setSubmenusVisibleItemsCount={setSubmenusVisibleItemsCount}
      />
    );
  },

  user_section_item: ({
    item,
    sidebarOpened,
    isSkeletonLoading,
    onMenuItemClick,
    pathname,
    isActive,
  }: SidebarItemMapperProps) => {
    if (!isUserSectionItem(item)) return null;

    return (
      <SidebarUserSectionItem
        item={item}
        isActive={isActive}
        pathname={pathname}
        sidebarOpened={sidebarOpened}
        isSkeletonLoading={isSkeletonLoading}
        onMenuItemClick={onMenuItemClick}
      />
    );
  },
};
